import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  IExternalEmployee,
  IFilterTimesheet,
  ITask,
  ITaskResponse,
  ITimesheetResponse,
  IUpdateTask,
} from "../models/Timesheet";
import { timesheetService } from "../services/timesheetService";
import { timesheetQueryKey } from "./QueryKey";

export function useGetTimesheetWeekByDate(
  params: IFilterTimesheet,
): UseQueryResult<ITimesheetResponse> {
  return useQuery({
    queryKey: [timesheetQueryKey.TIMESHEET, params],
    queryFn: () => timesheetService.getTimesheetWeek(params),
  });
}
export function useCreateTimesheet(): UseMutationResult<
  ITimesheetResponse,
  unknown,
  ITask,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ITask) => timesheetService.createTimesheet(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [timesheetQueryKey.TIMESHEET],
        predicate: (query) => query.queryKey[0] === timesheetQueryKey.TIMESHEET,
      });
    },
  });
}

export function useUpdateTimesheet(): UseMutationResult<
  ITaskResponse,
  unknown,
  { id: string; data: IUpdateTask },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IUpdateTask }) =>
      timesheetService.updateTimesheet(id, data),
    onSuccess: (responseData: ITaskResponse, variables) => {
      queryClient.setQueriesData(
        {
          queryKey: [timesheetQueryKey.TIMESHEET],
          predicate: (query) =>
            query.queryKey[0] === timesheetQueryKey.TIMESHEET,
        },
        (oldData: ITimesheetResponse | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            events: oldData.events.map((event) =>
              event.id === variables.id
                ? {
                    ...event,
                    title: responseData.taskName,
                    start: new Date(responseData.startDate)
                      .toISOString()
                      .split("T")[0],
                    end: new Date(responseData.endDate)
                      .toISOString()
                      .split("T")[0],
                    extendedProps: {
                      ...event.extendedProps,
                      description: responseData.description,
                      note: responseData.note,
                      status: responseData.status,
                    },
                  }
                : event,
            ),
          };
        },
      );
    },
  });
}
export function useDeleteTimesheet(): UseMutationResult<
  ITaskResponse,
  unknown,
  string,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => timesheetService.deleteTimesheet(id),
    onSuccess: (_, deletedId) => {
      queryClient.setQueriesData(
        {
          queryKey: [timesheetQueryKey.TIMESHEET],
          predicate: (query) =>
            query.queryKey[0] === timesheetQueryKey.TIMESHEET,
        },
        (oldData: ITimesheetResponse | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            events: oldData.events.filter((event) => event.id !== deletedId),
          };
        },
      );
    },
  });
}
export function useGetExternalEmployee(): UseQueryResult<IExternalEmployee[]> {
  return useQuery({
    queryKey: [timesheetQueryKey.EXTERNAL_EMPLOYEES],
    queryFn: () => timesheetService.getExternalEmployees(),
  });
}

export function useCreateExternalEmployee(): UseMutationResult<
  IExternalEmployee,
  unknown,
  Omit<IExternalEmployee, "id">,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<IExternalEmployee, "id">) =>
      timesheetService.createExternalEmployee(data),
    onSuccess: (data) => {
      queryClient.setQueryData(
        [timesheetQueryKey.EXTERNAL_EMPLOYEES],
        (oldData: IExternalEmployee[] | undefined) => {
          return [...(oldData || []), data];
        },
      );
    },
  });
}

export function useUpdateExternalEmployee(): UseMutationResult<
  IExternalEmployee,
  unknown,
  { id: number; data: Omit<IExternalEmployee, "id"> },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Omit<IExternalEmployee, "id">;
    }) => timesheetService.updateExternalEmployee(id, data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        {
          queryKey: [timesheetQueryKey.EXTERNAL_EMPLOYEES],
        },
        (oldData: IExternalEmployee[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((item) => (item.id === data.id ? data : item));
        },
      );
    },
  });
}

export function useDeleteExternalEmployee(): UseMutationResult<
  unknown,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => timesheetService.deleteExternalEmployee(id),
    onSuccess: (_, variables) => {
      queryClient.setQueriesData(
        {
          queryKey: [timesheetQueryKey.EXTERNAL_EMPLOYEES],
        },
        (oldData: IExternalEmployee[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.id !== variables);
        },
      );
    },
  });
}
